import React, { useCallback, useEffect, useState } from "react";
import { Alert, Modal, Select, Typography } from "antd";
import { CampusRef } from "../../services/schools/campus.types";
import {
  getCampuses,
  moveUserToCampus,
} from "../../services/schools/school.service";
import { moveClassroomToCampus } from "../../services/classrooms/classrooms.service";
import { useNotification } from "../../hooks/useNotification";
import { getApiErrorMessage } from "../../utils";

const { Text } = Typography;

export type MoveTarget =
  | { kind: "user"; id: number; label: string }
  | { kind: "classroom"; id: number; label: string };

interface MoveToCampusModalProps {
  open: boolean;
  onClose: () => void;
  /** Any id of the tree: `getCampuses` resolves the root from it. */
  rootSchoolId: number;
  /** Campus the target lives in today — never offered as a destination. */
  currentSchoolId: number;
  target: MoveTarget;
  onMoved: () => void;
}

const WARNING: Record<MoveTarget["kind"], { message: string; description: string }> =
  {
    user: {
      message: "Se van a desasignar las clases de otro plantel",
      description:
        "Al mover a la persona, las clases que tenga asignadas fuera del plantel destino se le desasignan. Las clases del plantel destino se conservan.",
    },
    classroom: {
      message: "Se mueven los alumnos y se desasignan los profes de otro plantel",
      description:
        "Los alumnos de la clase se mueven con ella al plantel destino. Los profesores que pertenezcan a otro plantel quedan desasignados y el código de la clase se recalcula con la abreviatura del plantel destino.",
    },
  };

const MoveToCampusModal: React.FC<MoveToCampusModalProps> = ({
  open,
  onClose,
  rootSchoolId,
  currentSchoolId,
  target,
  onMoved,
}) => {
  const [campuses, setCampuses] = useState<CampusRef[]>([]);
  const [targetSchoolId, setTargetSchoolId] = useState<number | undefined>(
    undefined
  );
  const [loading, setLoading] = useState<boolean>(false);
  const [moving, setMoving] = useState<boolean>(false);

  const { notify } = useNotification();
  // React 19 does not render the static `Modal.confirm`, so the confirmation
  // has to come from the hook API with its context holder mounted below.
  const [modal, modalContextHolder] = Modal.useModal();

  useEffect(() => {
    if (!open || !rootSchoolId) return;
    let cancelled = false;

    const loadCampuses = async () => {
      setLoading(true);
      try {
        const tree = await getCampuses(rootSchoolId);
        if (!cancelled) setCampuses(tree ?? []);
      } catch (error) {
        if (!cancelled) {
          setCampuses([]);
          notify(
            "error",
            "Error",
            getApiErrorMessage(error, "No se pudieron cargar los planteles.")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadCampuses();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rootSchoolId]);

  useEffect(() => {
    if (!open) setTargetSchoolId(undefined);
  }, [open]);

  const options = campuses
    .filter((campus) => campus.id !== currentSchoolId)
    .map((campus) => ({ value: campus.id, label: campus.name }));

  const describeUserResult = (disconnected: number): string =>
    disconnected === 0
      ? "No hubo clases para desasignar."
      : `${disconnected} ${
          disconnected === 1 ? "clase desasignada" : "clases desasignadas"
        }.`;

  const runMove = useCallback(async () => {
    if (!targetSchoolId) return;
    setMoving(true);
    try {
      if (target.kind === "user") {
        const result = await moveUserToCampus(target.id, targetSchoolId);
        notify(
          "success",
          "Usuario movido",
          `${target.label} ahora pertenece al plantel destino. ${describeUserResult(
            result?.disconnected_classrooms ?? 0
          )}`
        );
      } else {
        const result = await moveClassroomToCampus(target.id, targetSchoolId);
        notify(
          "success",
          "Clase movida",
          `${target.label} ahora pertenece al plantel destino. ${
            result.moved_students
          } ${
            result.moved_students === 1 ? "alumno movido" : "alumnos movidos"
          }, ${result.disconnected_teachers} ${
            result.disconnected_teachers === 1
              ? "profesor desasignado"
              : "profesores desasignados"
          }. Nuevo código: ${result.custom_code}.`
        );
      }
      onMoved();
      onClose();
    } catch (error) {
      notify(
        "error",
        "Error",
        getApiErrorMessage(error, "No se pudo mover al plantel destino.")
      );
    } finally {
      setMoving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, targetSchoolId, onMoved, onClose]);

  const handleConfirm = () => {
    if (!targetSchoolId) return;
    const destination = campuses.find((campus) => campus.id === targetSchoolId);
    modal.confirm({
      title: "Mover a plantel",
      content: `${target.label} pasa al plantel ${
        destination?.name ?? "destino"
      }. ${WARNING[target.kind].description}`,
      okText: "Sí, mover",
      cancelText: "Cancelar",
      onOk: runMove,
    });
  };

  return (
    <>
      {modalContextHolder}
      <Modal
        open={open}
        title="Mover a plantel"
        onCancel={onClose}
        onOk={handleConfirm}
        okText="Mover"
        cancelText="Cancelar"
        okButtonProps={{ disabled: !targetSchoolId, loading: moving }}
        destroyOnClose
      >
        <Text>{target.label}</Text>
        <Select
          style={{ width: "100%", marginTop: 16, marginBottom: 16 }}
          placeholder="Seleccionar plantel destino"
          aria-label="Plantel destino"
          value={targetSchoolId}
          onChange={setTargetSchoolId}
          loading={loading}
          options={options}
        />
        <Alert
          type="warning"
          showIcon
          message={WARNING[target.kind].message}
          description={WARNING[target.kind].description}
        />
      </Modal>
    </>
  );
};

export default MoveToCampusModal;
